/* Top-of-navbar looping marquee.
 *
 * Brian QA: "Limited 10% off APEX10... should be in loop on top of
 * navbar, black bg golden text animated marquee". Strip rolls across
 * the top of every page with the live promo + trust badges so buyers
 * see urgency + reasons to trust on every surface.
 *
 * Pure CSS keyframes — no JS, no listeners, no layout thrash. The
 * inner row is duplicated so the loop seam is invisible. Pause on
 * hover so accessibility-conscious readers can read the items. */

"use client";

import { usePathname } from "next/navigation";
import { SPIKER_VERIFIED_REVIEWS } from "@/lib/spiker-proof";

type Item = { kind: "promo" | "trust"; text: string };

/* Download/one-time products (default). */
const DEFAULT_ITEMS: Item[] = [
  { kind: "promo", text: "10% off your first order — use code APEX10 at checkout" },
  { kind: "trust", text: "✓ Instant download" },
  { kind: "trust", text: "✓ 14-day guarantee" },
  { kind: "trust", text: "✓ Print at home or any lab" },
  { kind: "promo", text: "30% off every digital file · Apex Pass →" },
  { kind: "trust", text: "✓ 300 DPI hi-res files" },
  { kind: "trust", text: "✓ Commercial license included" },
  { kind: "promo", text: "New prints daily · curated by hand" },
  { kind: "trust", text: "✓ Stripe-secured checkout" },
];

/* Apex Pulse is a SUBSCRIPTION — never show blanket no-recurring-fee /
 * instant-download / 300-DPI claims here (Pulse SOP compliance). */
const PULSE_ITEMS: Item[] = [
  { kind: "promo", text: "Apex Pulse — get your business cited by the AIs" },
  { kind: "trust", text: "✓ Cancel anytime" },
  { kind: "trust", text: "✓ Annual = 2 months free" },
  { kind: "trust", text: "✓ Works on any site" },
  { kind: "trust", text: "✓ No API keys" },
  { kind: "trust", text: "✓ Stripe-secured checkout" },
];

/* The managed AI receptionist is also a subscription. (/ai-staff is one-time,
 * so it keeps the default badges.) */
const AI_ITEMS: Item[] = [
  { kind: "promo", text: "AI Receptionist — answers your calls 24/7" },
  { kind: "trust", text: "✓ Free 30-day trial" },
  { kind: "trust", text: "✓ Cancel anytime, one click" },
  { kind: "trust", text: "✓ Real local number" },
  { kind: "trust", text: "✓ Stripe-secured checkout" },
];

/* Done-for-you websites: a one-time build you own + an optional Care Plan.
 * "Print at home / 300 DPI / instant download" is false here. */
const WEBSITE_ITEMS: Item[] = [
  { kind: "promo", text: "10% off your first order — use code APEX10 at checkout" },
  { kind: "promo", text: "Done-for-you websites — you own it" },
  { kind: "trust", text: "✓ You own your site + domain" },
  { kind: "trust", text: "✓ One-time build, no lock-in" },
  { kind: "trust", text: `✓ Built on the stack behind a real ${SPIKER_VERIFIED_REVIEWS.toLocaleString()}-review business` },
  { kind: "trust", text: "✓ 14-day make-it-right" },
  { kind: "trust", text: "✓ Stripe-secured checkout" },
];

/* Categories span planners, AI prompts, KDP interiors — "300 DPI / print at
 * home / new prints daily" is wall-art language. Use download-generic trust. */
const CATEGORY_ITEMS: Item[] = [
  { kind: "promo", text: "Every category, built niche-deep" },
  { kind: "trust", text: "✓ Instant download" },
  { kind: "trust", text: "✓ You own it" },
  { kind: "trust", text: "✓ Commercial license included" },
  { kind: "promo", text: "One Pass for every Apex store · 30% off digital →" },
  { kind: "trust", text: "✓ 14-day make-it-right" },
  { kind: "trust", text: "✓ Stripe-secured checkout" },
];

/* Business-in-a-Box pages sell a one-time digital toolkit — "print at home /
 * 300 DPI / new prints daily" is wall-art language (Business master template
 * §9). No income claims here either. */
const BUSINESS_ITEMS: Item[] = [
  { kind: "promo", text: "Business-in-a-Box — built on a real operator's system" },
  { kind: "trust", text: "✓ Instant access" },
  { kind: "trust", text: "✓ You own it · commercial license included" },
  { kind: "trust", text: "✓ One-time purchase" },
  { kind: "trust", text: "✓ 14-day make-it-right" },
  { kind: "trust", text: "✓ Stripe-secured checkout" },
];

/* Motion = copy-paste React/CSS components for developers. "Print at home /
 * 300 DPI / new prints daily" tells a dev the store doesn't know what it sells. */
const MOTION_ITEMS: Item[] = [
  { kind: "promo", text: "Apex Motion — copy-paste animated components" },
  { kind: "trust", text: "✓ Free tier — copy and ship" },
  { kind: "trust", text: "✓ Live previews" },
  { kind: "trust", text: "✓ Performance-first, no bloat" },
  { kind: "trust", text: "✓ You own the code" },
  { kind: "trust", text: "✓ Stripe-secured checkout" },
];

/* 3D models = STL to print + GLB to view. Not "300 DPI" wall-art language. */
const THREED_ITEMS: Item[] = [
  { kind: "promo", text: "Apex 3D — original models, STL + GLB" },
  { kind: "trust", text: "✓ STL to print + GLB to view" },
  { kind: "trust", text: "✓ Instant download" },
  { kind: "trust", text: "✓ You own the files" },
  { kind: "trust", text: "✓ Generated in-house" },
  { kind: "trust", text: "✓ Stripe-secured checkout" },
];

function itemsFor(path: string): Item[] {
  if (path.startsWith("/pulse")) return PULSE_ITEMS;
  if (path === "/" || path === "/cart" || path === "/pricing" || path.startsWith("/build") || path.startsWith("/forge") || path.startsWith("/b/") || path.startsWith("/websites") || path.startsWith("/website-gallery") || path.startsWith("/w/")) return WEBSITE_ITEMS;
  if (path.startsWith("/categories")) return CATEGORY_ITEMS;
  if (path.startsWith("/business") || path.startsWith("/browse-businesses")) return BUSINESS_ITEMS;
  if (path.startsWith("/motion") || path.startsWith("/components")) return MOTION_ITEMS;
  if (path.startsWith("/3d")) return THREED_ITEMS;
  if (path.startsWith("/ai") && !path.startsWith("/ai-staff")) return AI_ITEMS;
  return DEFAULT_ITEMS;
}

export function MarqueeBanner() {
  const pathname = usePathname() || "/";
  const ITEMS = itemsFor(pathname);
  /* Render the row twice in the DOM. The CSS animation slides the
   * track by exactly -50% so the second copy is in the spot the first
   * one started — visually seamless. */
  const row = (
    <div className="flex shrink-0 items-center gap-12 px-6 py-2.5">
      {ITEMS.map((item, i) => (
        <span
          key={i}
          className="inline-flex items-center whitespace-nowrap font-mono text-[12px] uppercase"
          style={{
            letterSpacing: "0.12em",
            color:
              item.kind === "promo"
                ? "var(--color-accent)"
                : "rgba(255,255,255,0.78)",
            fontWeight: item.kind === "promo" ? 600 : 400,
          }}
        >
          {item.text}
          <span
            aria-hidden
            className="ml-12 inline-block h-1 w-1 rounded-full"
            style={{ background: "rgba(255,255,255,0.25)" }}
          />
        </span>
      ))}
    </div>
  );

  return (
    <div
      className="relative w-full overflow-hidden border-b"
      style={{
        background: "#000",
        borderColor: "rgba(217,163,90,0.25)",
      }}
    >
      {/* SOP Phase 0.5 — duplicate row is the standard infinite-scroll
       * pattern (track width 2x + translateX -50% = seamless loop).
       * aria-hidden on the clone keeps screen readers from reading the
       * items twice and prevents accidental duplicate-content signals. */}
      <div className="ax-marquee-track flex">
        {row}
        <div aria-hidden="true" className="contents">{row}</div>
      </div>

      <style>{`
        @keyframes ax-marquee {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        .ax-marquee-track {
          width: max-content;
          animation: ax-marquee 38s linear infinite;
          will-change: transform;
        }
        .ax-marquee-track:hover {
          animation-play-state: paused;
        }
        @media (prefers-reduced-motion: reduce) {
          .ax-marquee-track {
            animation-duration: 90s;
          }
        }
      `}</style>
    </div>
  );
}
